import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";

const clean = (value) => String(value ?? "").trim();

export default function NovelPhysicalPaginationStudyV3C2Panel({
  storyId = "", previewOnly = false,
  title = "", authorName = "", bookFormat,
  frontMatter, chapters, backMatter, sceneById, isFR = false,
}) {
  const [state, setState] = useState({ status: "idle", report: null, error: "" });
  const requestGeneration = useRef(0);
  useEffect(() => {
    // Invalidate previous on-demand evidence as soon as saved-source UI inputs
    // change or the owner switches books. Ignore late response after unmount.
    requestGeneration.current += 1;
    setState({ status: "idle", report: null, error: "" });
    return () => { requestGeneration.current += 1; };
  }, [storyId, title, authorName, bookFormat, frontMatter, chapters, backMatter, sceneById]);

  async function runPrivateStudy() {
    if (!clean(storyId) || previewOnly || state.status === "loading") return;
    const generation = ++requestGeneration.current;
    setState({ status: "loading", report: null, error: "" });
    try {
      const response = await api.get(
        `/api/stories/${encodeURIComponent(storyId)}/novel-physical-study-v3c2`
      );
      if (generation !== requestGeneration.current) return;
      if (!response?.ok || !response?.report ||
          response.report.schemaVersion !== "novel-physical-study-report-v3c2b") {
        throw new Error(isFR ? "Rapport d'étude invalide." : "Unexpected private study report.");
      }
      setState({ status: "ready", report: response.report, error: "" });
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      setState({ status: "error", report: null,
        error: clean(error?.message) ||
          (isFR ? "L'étude ne peut pas être calculée." : "Unable to measure the saved Novel.") });
    }
  }

  const report = state.status === "ready" ? state.report : null;
  const label = (en, fr) => isFR ? fr : en;
  return (
    <article className="ns-card" data-novel-physical-study-v3c2b="owner-on-demand"
      style={{ padding: 20, borderRadius: 16, border: "1px solid #cbd5e1" }}>
      <h3 style={{ margin: "0 0 8px" }}>
        {label("V3C2 · Physical Text Pagination Study (Private)",
          "V3C2 · Étude de pagination physique du texte (privée)")}
      </h3>
      <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
        {label("On-demand measurement from the saved Novel using the shared physical PDFKit text-line and page-geometry engine. This is not an approved interior PDF or final print pagination. No book changes occur.",
          "Mesure à la demande du roman enregistré. Ceci n'est ni un PDF approuvé ni une pagination finale ; aucune modification du livre.")}
      </p>
      <button type="button" className="ns-button ns-button-outline"
        data-novel-physical-study-v3c2b-run="true"
        disabled={!clean(storyId) || previewOnly || state.status === "loading"}
        onClick={runPrivateStudy}>
        {state.status === "loading"
          ? label("Measuring Saved Text…", "Mesure du texte enregistré…")
          : label("Run Physical Text Study", "Lancer l'étude physique du texte")}
      </button>
      {previewOnly ? (
        <p className="ns-card-meta" style={{ margin: "8px 0" }}>
          {label("Unavailable for unsaved preview projects.", "Indisponible pour les aperçus non enregistrés.")}
        </p>
      ) : null}
      {state.status === "error" ? (
        <p role="alert" data-novel-physical-study-v3c2b-blocked="true"
          style={{ margin: "12px 0 0", color: "#92400e" }}>
          <strong>{label("Study blocked:", "Étude bloquée :")}</strong>{" "}{state.error}
        </p>
      ) : null}
      {report ? (
        <div data-novel-physical-study-v3c2b-report="unapproved"
          style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <p style={{ margin: "0 0 10px" }}>
            <strong>{label("Provisional physical text study", "Étude physique provisoire du texte")}:</strong>{" "}
            {report.provisionalPhysicalTextStudyPages} {label("study pages", "pages d'étude")}
            {" · "}{report.sourceSectionCount} {label("saved sections", "sections enregistrées")}
            {" · "}{report.sourceSceneCount} {label("Canon Scenes", "scènes Canon")}
          </p>
          <p className="ns-card-meta" style={{ margin: "0 0 10px" }}>
            {report.savedFrontMatterCount} {label("Front Matter", "Préliminaires")}
            {" · "}{report.savedChapterCount} {label("Chapters", "Chapitres")}
            {" · "}{report.savedBackMatterCount} {label("Back Matter", "Postliminaires")}
            {" · "}{label("Author", "Auteur")}: {clean(report.authorName)}
            {" · "}{label("Trim", "Format")}: {clean(report.trimKey)}
            {" · "}{label("Margin study converged", "Marges d'étude convergentes")}:{" "}
            {report.marginStudyConverged ? label("Yes", "Oui") : label("No", "Non")}
          </p>
          <details data-novel-physical-study-v3c2b-spans="true">
            <summary style={{ cursor: "pointer", fontWeight: 700 }}>
              {label("Inspect Provisional Physical Section Spans", "Inspecter les plages physiques provisoires")}
            </summary>
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {(Array.isArray(report.units) ? report.units : []).map((unit, index) => (
                <div key={unit.kind + ":" + unit.sourceId}
                  className="ns-card-meta"
                  style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 7 }}>
                  <strong>{index + 1}. {unit.title}</strong>
                  {" · "}{unit.measuredStudyPageSpan} {label("text-study pages", "pages d'étude du texte")}
                  {" · "}{unit.firstStudyPage}–{unit.lastStudyPage}
                  {" · "}{label("new-page start", "nouvelle page")}
                </div>
              ))}
            </div>
          </details>
        </div>
      ) : null}
      <p data-novel-physical-study-v3c2b-production-gate="disabled"
        style={{ margin: "12px 0 0", fontWeight: 700, color: "#92400e" }}>
        {label("Not a verified physical print-page count. No approved PDF, manuscript lock, production margins, spine calculation, export or publication.",
          "Pas de pagination imprimée définitive. Aucun PDF approuvé, verrouillage, marges finales, dos, export ou publication.")}
      </p>
    </article>
  );
}
